import React, { useState, useEffect, useCallback } from 'react';
import { communityApi, getAuthSession } from '../services/api';

const CATEGORIES = [
  'Mental Health',
  'Clinical Insights',
  'Therapy & Coping',
  'Mindfulness & Peace',
  'Success Story',
  'Support & Advice',
  'General Discussion',
];

function TherapistCommunityPostManager() {
  const authSession = getAuthSession();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'my' | 'therapist'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // New Post Form State
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Clinical Insights',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [detectedProhibited, setDetectedProhibited] = useState([]);
  const [isCheckingWords, setIsCheckingWords] = useState(false);

  // Status & Error Messages
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Comments State
  const [expandedCommentsPostId, setExpandedCommentsPostId] = useState(null);
  const [commentInput, setCommentInput] = useState({});
  const [submittingCommentId, setSubmittingCommentId] = useState(null);

  const currentUserId = authSession?.user?._id || authSession?.user?.id;

  const formatDateSafe = (dateVal) => {
    if (!dateVal) return 'Recent';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return 'Recent';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return 'Recent';
    }
  };

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const res = await communityApi.getPosts({
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchQuery || undefined,
      });

      const list = Array.isArray(res) ? res : (res?.posts || res?.data || []);
      setPosts(list);
    } catch (err) {
      console.error('Failed to load community posts:', err);
      setErrorMessage(err.message || 'Failed to load community posts.');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, searchQuery]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Real-time verify text against prohibited words with debounce
  useEffect(() => {
    if (!formData.content.trim() && !formData.title.trim()) {
      setDetectedProhibited([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsCheckingWords(true);
        const fullText = `${formData.title} ${formData.content}`;
        const scanRes = await communityApi.verifyText(fullText);
        if (scanRes?.hasProhibitedWords && scanRes.foundWords) {
          setDetectedProhibited(scanRes.foundWords);
        } else {
          setDetectedProhibited([]);
        }
      } catch (err) {
        // Silently catch live scanner errors
      } finally {
        setIsCheckingWords(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.title, formData.content]);

  const handleCreatePostSubmit = async (e) => {
    e.preventDefault();
    if (!formData.content.trim()) {
      setErrorMessage('Please enter your post content.');
      return;
    }

    if (detectedProhibited.length > 0) {
      setErrorMessage(`Please remove prohibited words before publishing: ${detectedProhibited.map(w => `"${w.word}"`).join(', ')}`);
      return;
    }

    try {
      setSubmitting(true);
      setErrorMessage('');

      const res = await communityApi.createPost(formData);
      setStatusMessage('✓ Community post published successfully with verified therapist badge!');
      setTimeout(() => setStatusMessage(''), 4000);

      setFormData({
        title: '',
        category: 'Clinical Insights',
        content: '',
      });
      setIsCreating(false);
      setDetectedProhibited([]);
      if (res?.post) {
        setPosts(prev => [res.post, ...prev.filter(p => p._id !== res.post._id)]);
      }
      fetchPosts();
    } catch (err) {
      console.error('Failed to publish post:', err);
      if (err?.isProhibited && err?.foundWords) {
        setDetectedProhibited(err.foundWords);
      }
      setErrorMessage(err.message || 'Failed to publish post.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!currentUserId) {
      alert('Please log in to like posts');
      return;
    }
    try {
      const res = await communityApi.likePost(postId);
      setPosts(prev => prev.map(p => {
        if (p._id === postId) {
          return {
            ...p,
            likesCount: res.likesCount,
            likes: res.isLiked
              ? [...(p.likes || []), currentUserId]
              : (p.likes || []).filter(id => String(id) !== String(currentUserId)),
          };
        }
        return p;
      }));
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleAddComment = async (postId) => {
    const text = commentInput[postId];
    if (!text || !text.trim()) return;

    try {
      setSubmittingCommentId(postId);
      const res = await communityApi.addComment(postId, { content: text.trim() });
      setPosts(prev => prev.map(p => {
        if (p._id === postId) {
          return {
            ...p,
            comments: res.comments,
            commentsCount: res.commentsCount,
          };
        }
        return p;
      }));
      setCommentInput(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      alert(err.message || 'Failed to add comment');
    } finally {
      setSubmittingCommentId(null);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this community post?')) return;
    try {
      await communityApi.deletePost(postId);
      setPosts(prev => prev.filter(p => p._id !== postId));
      setStatusMessage('✓ Post deleted.');
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to delete post');
    }
  };

  const getAuthorId = (post) => {
    if (!post) return '';
    if (post.author && typeof post.author === 'object') {
      return post.author._id || post.author.id || '';
    }
    return post.author || '';
  };

  const isMyPost = (post) => {
    if (!currentUserId || !post) return false;
    const authorId = getAuthorId(post);
    return String(authorId).toLowerCase() === String(currentUserId).toLowerCase() || authSession?.user?.role === 'admin';
  };

  const myPostsCount = posts.filter(p => isMyPost(p)).length;
  const therapistPostsCount = posts.filter(p => p.authorRole === 'physiotherapist' || p.authorRole === 'therapist' || p.isVerifiedTherapist).length;

  const displayedPosts = posts.filter(p => {
    if (filterTab === 'my') {
      return isMyPost(p);
    }
    if (filterTab === 'therapist') {
      return p.authorRole === 'physiotherapist' || p.authorRole === 'therapist' || p.isVerifiedTherapist;
    }
    return true;
  });

  return (
    <div style={{ background: '#ffffff', borderRadius: '16px', padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', marginTop: '20px' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
            💬 Community Sharing & Insights Wall
          </h3>
          <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b' }}>
            Share clinical expertise, coping techniques, and mental wellness guidance. Posts are automatically verified against community safety guidelines.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsCreating(!isCreating)}
          style={{
            padding: '11px 22px',
            borderRadius: '10px',
            background: isCreating ? '#64748b' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
            color: '#ffffff',
            border: 'none',
            fontWeight: 700,
            fontSize: '13.5px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>{isCreating ? '✕ Close Form' : '✍️ + Create Community Post'}</span>
        </button>
      </div>

      {statusMessage && (
        <div style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '12px 18px', borderRadius: '10px', marginBottom: '18px', fontSize: '13.5px', fontWeight: 600 }}>
          {statusMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '12px 18px', borderRadius: '10px', marginBottom: '18px', fontSize: '13.5px', fontWeight: 600 }}>
          {errorMessage}
        </div>
      )}

      {/* CREATE POST BOX WITH LIVE VERIFICATION SCANNER */}
      {isCreating && (
        <div style={{
          background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
          border: '2px solid #d8b4fe',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '28px',
          boxShadow: '0 8px 24px rgba(124, 58, 237, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>✍️</span>
              <h4 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#581c87' }}>
                Publish Clinical Post to Community
              </h4>
            </div>
            <span style={{ background: '#7c3aed', color: '#fff', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '20px' }}>
              🧑‍⚕️ Verified Therapist Post
            </span>
          </div>

          <form onSubmit={handleCreatePostSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#4c1d95', marginBottom: '6px' }}>
                  Post Title (Optional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. 5 Daily Grounding Exercises for Acute Anxiety"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13.5px', background: '#fff' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#4c1d95', marginBottom: '6px' }}>
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13px', fontWeight: 700, background: '#fff', color: '#581c87' }}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#4c1d95', marginBottom: '6px' }}>
                Post Body / Content *
              </label>
              <textarea
                rows="5"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Share your therapeutic advice, exercises, or mental health perspectives with the community..."
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: detectedProhibited.length > 0 ? '2px solid #ef4444' : '1.5px solid #d8b4fe',
                  fontSize: '13.5px',
                  lineHeight: '1.6',
                  background: '#fff',
                }}
              />
            </div>

            {/* LIVE SAFETY VERIFICATION STATUS BADGE */}
            <div style={{ marginBottom: '18px' }}>
              {isCheckingWords ? (
                <div style={{ fontSize: '12px', color: '#6b21a8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⏳ Scanning text against prohibited words list...</span>
                </div>
              ) : detectedProhibited.length > 0 ? (
                <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: '10px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🚫 Prohibited Words Detected:</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                    {detectedProhibited.map((item, idx) => (
                      <span key={idx} style={{ background: '#fee2e2', color: '#b91c1c', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 800, border: '1px solid #f87171' }}>
                        "{item.word}" ({item.category})
                      </span>
                    ))}
                  </div>
                  <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#7f1d1d' }}>
                    Please edit your post to remove these words. Community guidelines prohibit explicit vulgarity, violence, and non-verified clinical claims.
                  </p>
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                  <span>🛡️ Verified: No prohibited words detected in your text.</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, cursor: 'pointer' }}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting || detectedProhibited.length > 0}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  background: detectedProhibited.length > 0 ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  cursor: detectedProhibited.length > 0 ? 'not-allowed' : 'pointer',
                  boxShadow: detectedProhibited.length > 0 ? 'none' : '0 4px 12px rgba(124, 58, 237, 0.3)',
                }}
              >
                {submitting ? 'Publishing...' : '🚀 Publish Community Post'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FILTER & TABS */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
          <button
            type="button"
            onClick={() => setFilterTab('all')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              background: filterTab === 'all' ? '#fff' : 'transparent',
              color: filterTab === 'all' ? '#7c3aed' : '#64748b',
              boxShadow: filterTab === 'all' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            All Posts ({posts.length})
          </button>
          <button
            type="button"
            onClick={() => setFilterTab('my')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              background: filterTab === 'my' ? '#fff' : 'transparent',
              color: filterTab === 'my' ? '#7c3aed' : '#64748b',
              boxShadow: filterTab === 'my' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            My Posts ({myPostsCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterTab('therapist')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              background: filterTab === 'therapist' ? '#fff' : 'transparent',
              color: filterTab === 'therapist' ? '#7c3aed' : '#64748b',
              boxShadow: filterTab === 'therapist' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            🧑‍⚕️ Therapists Only ({therapistPostsCount})
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12.5px', fontWeight: 600, background: '#fff' }}
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12.5px', width: '180px' }}
          />
        </div>
      </div>

      {/* POSTS LIST */}
      {loading ? (
        <div style={{ padding: '50px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
          Loading community posts...
        </div>
      ) : displayedPosts.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
          <div style={{ fontSize: '42px', marginBottom: '10px' }}>💬</div>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#334155' }}>No community posts found</h4>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
            Be the first practitioner to share a clinical post or wellness technique!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {displayedPosts.map((post) => {
            const authorId = post.author?._id || post.author?.id || post.author;
            const isMyPost = currentUserId && (String(authorId).toLowerCase() === String(currentUserId).toLowerCase() || authSession?.user?.role === 'admin');
            const isLikedByMe = currentUserId && (post.likes || []).some(id => String(id).toLowerCase() === String(currentUserId).toLowerCase());
            const isExpanded = expandedCommentsPostId === post._id;
            const authorDisplayName = post.authorName || post.author?.name || 'Practitioner';
            const authorInitial = String(authorDisplayName).trim().charAt(0).toUpperCase() || 'U';
            const isVerified = Boolean(post.isVerifiedTherapist || post.author?.isVerified || post.authorRole === 'physiotherapist' || post.authorRole === 'therapist');

            return (
              <div
                key={post._id || post.id || Math.random()}
                style={{
                  background: '#ffffff',
                  border: isVerified ? '1.5px solid #e9d5ff' : '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '20px 24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
              >
                {/* Author Info Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      background: isVerified ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#94a3b8',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '16px',
                    }}>
                      {authorInitial}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '14.5px', color: '#0f172a' }}>
                          {authorDisplayName}
                        </span>
                        {isVerified && (
                          <span style={{ fontSize: '11px', fontWeight: 800, background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: '12px', border: '1px solid #d8b4fe' }}>
                            ✓ Verified Practitioner
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        {post.therapistSpecialization ? `${post.therapistSpecialization} • ` : (post.author?.specialization ? `${post.author.specialization} • ` : '')}
                        {formatDateSafe(post.createdAt || post.updatedAt)}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11.5px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: '#eff6ff', color: '#1e40af' }}>
                      {post.category}
                    </span>
                    {isMyPost && (
                      <button
                        type="button"
                        onClick={() => handleDeletePost(post._id)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '4px 8px' }}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>

                {/* Post Content */}
                {post.title && (
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>
                    {post.title}
                  </h4>
                )}
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {post.content}
                </p>

                {/* Action Bar: Like & Comment */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => handleLike(post._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: isLikedByMe ? '#dc2626' : '#64748b',
                    }}
                  >
                    <span>{isLikedByMe ? '❤️' : '🤍'}</span>
                    <span>{post.likesCount || 0} Likes</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setExpandedCommentsPostId(isExpanded ? null : post._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#4f46e5',
                    }}
                  >
                    <span>💬</span>
                    <span>{post.commentsCount || (post.comments || []).length} Comments</span>
                  </button>
                </div>

                {/* Expanded Comments Section */}
                {isExpanded && (
                  <div style={{ marginTop: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                      {(post.comments || []).length === 0 ? (
                        <div style={{ fontSize: '12.5px', color: '#94a3b8', fontStyle: 'italic' }}>
                          No comments yet. Start the conversation!
                        </div>
                      ) : (
                        post.comments.map((c, cIdx) => (
                          <div key={cIdx} style={{ background: '#fff', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontWeight: 800, fontSize: '12.5px', color: '#1e293b' }}>
                                {c.authorName} {c.authorRole === 'physiotherapist' ? '(Practitioner)' : ''}
                              </span>
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : 'Recent'}
                              </span>
                            </div>
                            <div style={{ fontSize: '13px', color: '#475569' }}>
                              {c.content}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add Comment Input */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder="Write a supportive reply or clinical tip..."
                        value={commentInput[post._id] || ''}
                        onChange={(e) => setCommentInput({ ...commentInput, [post._id]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddComment(post._id);
                          }
                        }}
                        style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddComment(post._id)}
                        disabled={submittingCommentId === post._id || !commentInput[post._id]?.trim()}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          background: '#7c3aed',
                          color: '#fff',
                          border: 'none',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          cursor: 'pointer',
                        }}
                      >
                        {submittingCommentId === post._id ? 'Sending...' : 'Reply'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TherapistCommunityPostManager;
